import {
  PropertyType,
  PropertyOperation,
  PropertySharingField,
  PropertySharingExactNamespace,
} from 'src/api/generated';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import {
  PropertySharingTextPart,
  type PropertySharingExternalSite,
  PropertySharingFieldPart,
  PropertySharingIdentifierPart,
  type PropertySharingTemplatePart,
} from 'src/api/generated';
import type { PropertyLinkPartLocation } from '../types/property-links.types';

/** Stable identity of one selected variable, independent of example or mapped values.
 * @param parameter Selected field or identifier.
 * @returns Canonical key for the generated parameter shape.
 */
export function propertyLinkParameterKey(
  parameter: PropertySharingFieldPart | PropertySharingIdentifierPart
): string {
  return parameter.kind === PropertySharingFieldPart.kind.PROPERTY
    ? JSON.stringify([parameter.kind, parameter.field])
    : JSON.stringify([
        parameter.kind,
        parameter.field,
        parameter.selector.system,
        parameter.selector.kind,
        parameter.selector.label,
        parameter.selector.namespace.mode,
        parameter.selector.namespace.mode ===
        PropertySharingExactNamespace.mode.EXACT
          ? parameter.selector.namespace.value
          : null,
      ]);
}

/** Separate literal route separators from editable segments without decoding their data.
 * @param parts Stored path parts, including legacy parts containing multiple segments.
 * @returns Equivalent path with each literal separator isolated from editable text.
 */
export function splitPropertyLinkPath(
  parts: PropertySharingTemplatePart[]
): PropertySharingTemplatePart[] {
  return parts.flatMap<PropertySharingTemplatePart>((part) =>
    part.kind === PropertySharingTextPart.kind.TEXT
      ? part.value
          .split(/(\/)/u)
          .filter(Boolean)
          .map((value) => ({ ...part, value }))
      : [part]
  );
}

/** Parse a real URL as literal parts; customers explicitly select every variable.
 * @param value Customer-provided complete website URL.
 * @returns Literal template, or null for unsupported input.
 */
export function parsePropertyLinkExample(
  value: string
): PropertySharingExternalSite | null {
  try {
    const input = value.trim();
    const rawPath = /^[a-z]+:\/\/[^/?#]+([^?#]*)/iu.exec(input)?.[1] ?? '';
    if (
      input.includes('#') ||
      /%(?![\da-f]{2})/iu.test(input) ||
      rawPath.split('/').some((part) => /^(?:\.|%2e){1,2}$/iu.test(part))
    ) {
      return null;
    }
    const url = new URL(input);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.hash ||
      [...value.trim()].some(
        (char) =>
          char === '\\' ||
          char.charCodeAt(0) <= 32 ||
          char.charCodeAt(0) === 127
      )
    ) {
      return null;
    }
    const queryIndex = input.indexOf('?');
    return {
      origin: url.origin,
      path: splitPropertyLinkPath([
        { kind: PropertySharingTextPart.kind.TEXT, value: url.pathname },
      ]),
      query:
        queryIndex < 0
          ? []
          : input
              .slice(queryIndex + 1)
              .split('&')
              .map((entry) => {
                const equals = entry.indexOf('=');
                return {
                  key: equals < 0 ? entry : entry.slice(0, equals),
                  value: [
                    {
                      kind: PropertySharingTextPart.kind.TEXT,
                      value: equals < 0 ? '' : entry.slice(equals + 1),
                    },
                  ],
                  encoded: true,
                  ...(equals < 0 ? { omitEquals: true } : {}),
                };
              }),
    };
  } catch {
    return null;
  }
}

/** Collect distinct variables in their first-occurrence order.
 * @param site Current template.
 * @returns Distinct variables in display order.
 */
export function propertyLinkParameters(
  site: PropertySharingExternalSite
): (PropertySharingFieldPart | PropertySharingIdentifierPart)[] {
  const parameters = new Map<
    string,
    PropertySharingFieldPart | PropertySharingIdentifierPart
  >();
  for (const part of [
    ...site.path,
    ...site.query.flatMap(({ value }) => value),
  ]) {
    if (part.kind !== PropertySharingTextPart.kind.TEXT) {
      parameters.set(propertyLinkParameterKey(part), part);
    }
  }
  return [...parameters.values()];
}

/** Replace one occurrence and retain mappings only for variables still used by the template.
 * @param site Current template.
 * @param location Exact path or query occurrence.
 * @param replacement Explicitly selected literal and variable parts.
 * @returns New template without altering other occurrences.
 */
export function replacePropertyLinkPart(
  site: PropertySharingExternalSite,
  location: PropertyLinkPartLocation,
  replacement: PropertySharingTemplatePart[]
): PropertySharingExternalSite {
  const replace = (parts: PropertySharingTemplatePart[]) =>
    parts.flatMap((part, index) =>
      index === location.partIndex ? replacement : [part]
    );
  const next =
    location.queryIndex === null
      ? { ...site, path: replace(site.path) }
      : {
          ...site,
          query: site.query.map((entry, index) =>
            index === location.queryIndex
              ? { ...entry, value: replace(entry.value) }
              : entry
          ),
        };
  const used = new Set(
    propertyLinkParameters(next).map(propertyLinkParameterKey)
  );
  return {
    ...next,
    ...(next.valueMappings
      ? {
          valueMappings: next.valueMappings.filter(({ parameter }) =>
            used.has(propertyLinkParameterKey(parameter))
          ),
        }
      : {}),
  };
}

/** Fictional values for preview only; never infer mappings from these values.
 * @param parameter Selected variable.
 * @returns An editable fictional value, never persisted as configuration.
 */
export function propertyLinkExampleValue(
  parameter: PropertySharingFieldPart | PropertySharingIdentifierPart
): string {
  if (parameter.kind === PropertySharingIdentifierPart.kind.IDENTIFIER) {
    return 'EXT-12345';
  }
  const values: Record<PropertySharingField, string> = {
    id: '12345',
    sourceId: 'EXT-12345',
    sourceCode: 'COD-12345',
    sourceSystem: 'EXAMPLE',
    mercadoLibreCode: 'MLC12345',
    name: 'Casa de ejemplo',
    locality: 'Santiago',
    localityCode: '13101',
    region: 'Metropolitana',
    regionCode: 'RM',
    countryCode: 'CL',
    neighborhood: 'Barrio de ejemplo',
    operation: 'SALE',
    propertyType: 'HOUSE',
  };
  return values[parameter.field];
}

/** Identify reusable categories whose values can differ between Comos and a website.
 * @param parameter Selected property field or external identifier.
 * @returns Whether the builder offers value mappings for this variable.
 */
export function propertyLinkSupportsValueMappings(
  parameter: PropertySharingFieldPart | PropertySharingIdentifierPart | null
): boolean {
  return (
    parameter?.kind === PropertySharingFieldPart.kind.PROPERTY &&
    [
      PropertySharingField.OPERATION,
      PropertySharingField.PROPERTY_TYPE,
      PropertySharingField.COUNTRY_CODE,
      PropertySharingField.REGION_CODE,
      PropertySharingField.LOCALITY_CODE,
      PropertySharingField.LOCALITY,
      PropertySharingField.REGION,
      PropertySharingField.NEIGHBORHOOD,
      PropertySharingField.SOURCE_SYSTEM,
    ].includes(parameter.field)
  );
}

/** Known categorical values for explicit mappings and fictional examples; never inferred translations.
 * @param parameter Selected variable.
 * @param t Current translation function.
 * @returns Supported category options, or an empty list for free text.
 */
export function propertyLinkCategoryOptions(
  parameter: PropertySharingFieldPart | PropertySharingIdentifierPart,
  t: I18nContextValue['t']
) {
  if (parameter.kind !== PropertySharingFieldPart.kind.PROPERTY) {
    return [];
  }
  if (parameter.field === PropertySharingField.PROPERTY_TYPE) {
    return Object.values(PropertyType).map((value) => ({
      value,
      content: t(`catalog.properties.propertyType.${value}`),
    }));
  }
  if (parameter.field === PropertySharingField.OPERATION) {
    return Object.values(PropertyOperation).map((value) => ({
      value,
      content: t(`catalog.properties.operation.${value}`),
    }));
  }
  return [];
}

/** Decode a literal only for editing; malformed legacy encodings remain visible as stored. */
export function decodePropertyLinkText(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Encode edited segment or query text as data; route separators are separate parts. */
export function encodePropertyLinkText(value: string): string {
  return encodeURIComponent(value);
}
